import {
  type IconName,
  Pill,
  ToggleButtonGroup,
  type ToggleButtonGroupProps,
} from '@wandb/components';
import {clamp, isEmpty} from 'lodash';
import React from 'react';

import {
  CREATED_COMPARE_MEDIA_PANEL_FEATURE_KEY,
  useOnboardingContext,
} from '../../../../../util/userContext';
import {
  type CompareSettings,
  type LayoutMode as PanelModes,
  type MediaBrowserPanelConfig,
} from '../../../../PanelMediaBrowser/common';
import * as PANEL_MEDIA_BROWSER_CONFIG from '../../../../PanelMediaBrowser/config';
import {useUpdateConfigWithMediaKeys} from '../../../../PanelMediaBrowser/hooks/useUpdateConfigWithMediaKeys';
import {getModeConfig} from '../../../../PanelMediaBrowser/modes/getModeConfig';
import {
  useMediaModeDisplay,
  useShouldShowMediaModeResetAtLevel,
} from '../../hooks';
import {SettingTitle} from '../common/SettingTitle';
import {useMediaSettingsContext} from '../contexts/MediaSettingsContextProvider';
import {SettingsLevel} from '../types';
import {IMAGE_FIT_AND_RESOLUTION_CONFIG_FIELDS} from './ImageFitAndResolution';

type PanelModeOption = {
  value: PanelModes;
  label: string;
  icon: IconName;
};

type PanelModeToggleItem = ToggleButtonGroupProps['items'][number] & {
  id: PanelModes;
};

const doCompareSettingsRequireSingleKey = (
  compareSettings: CompareSettings
) => {
  return (
    isEmpty(compareSettings) ||
    compareSettings?.tileControls?.mediaKey === 'linked'
  );
};

export const LayoutMode = () => {
  const {
    configWithoutDefaults,
    updateConfig,
    unsetConfigField,
    settingsLevel,
    panelRef,
  } = useMediaSettingsContext();
  const currentMode = useMediaModeDisplay(settingsLevel, panelRef);
  const isSetAtLevel = useShouldShowMediaModeResetAtLevel(
    settingsLevel,
    panelRef
  );
  const updateConfigWithMediaKeys = useUpdateConfigWithMediaKeys();
  const {
    onboardingState: createdCompareMediaPanelState,
    setOnboardingState: setCreatedCompareMediaPanelState,
  } = useOnboardingContext(CREATED_COMPARE_MEDIA_PANEL_FEATURE_KEY);
  // "new" pill indicator next to layout mode setting
  let newPillCta: React.ReactNode | undefined = undefined;

  const options: PanelModeOption[] = [
    {
      value: 'gallery',
      label: 'Gallery',
      icon: 'photo-picture',
    },
    {value: 'grid', label: 'Grid', icon: 'layout-grid'},
  ];
  if (settingsLevel === SettingsLevel.PANEL) {
    options.push({value: 'compare', label: 'Compare', icon: 'miller-columns'});
    // add the new pill cta if user has not yet used compare mode
    if (!createdCompareMediaPanelState.createdCompareMediaPanel) {
      newPillCta = (
        <span className="font-normal text-cactus-600">
          <Pill color="cactus" ml={2} textValue="New" />
        </span>
      );
    }
  }

  const onChangeMode = (mode: PanelModes | undefined) => {
    if (mode == null || mode === currentMode) {
      return;
    }
    const updates: Partial<MediaBrowserPanelConfig> = {mode};
    updates.page = {start: 0};
    if (mode === 'compare') {
      const compareSettings = configWithoutDefaults.compareSettings ?? {};
      const currentMediaKeys = configWithoutDefaults.mediaKeys ?? [];
      // if compareSettings already set, assume key configuration is intentional
      if (
        doCompareSettingsRequireSingleKey(compareSettings) &&
        currentMediaKeys.length > 1
      ) {
        updates.mediaKeys = [currentMediaKeys[0]];
      }
      if (configWithoutDefaults.columnCount) {
        updates.columnCount = clamp(
          configWithoutDefaults.columnCount,
          PANEL_MEDIA_BROWSER_CONFIG.MIN_COMPARE_COL_COUNT,
          PANEL_MEDIA_BROWSER_CONFIG.MAX_COMPARE_COL_COUNT
        );
      }
      setCreatedCompareMediaPanelState({
        ...createdCompareMediaPanelState,
        createdCompareMediaPanel: true,
      });
    }
    // If the mode does not support the image fit and resolution setting, unset it
    const newModeConfig = getModeConfig(mode);
    const imageFitResolutionField = IMAGE_FIT_AND_RESOLUTION_CONFIG_FIELDS.find(
      field => configWithoutDefaults[field] === true
    );
    if (
      imageFitResolutionField &&
      !newModeConfig.imageFitAndResolutionConfigFields.includes(
        imageFitResolutionField
      )
    ) {
      unsetConfigField(imageFitResolutionField);
    }

    updateConfigWithMediaKeys(updates, configWithoutDefaults, updateConfig);
  };

  const items: PanelModeToggleItem[] = options.map(option => ({
    id: option.value,
    children: option.label,
    icon: option.icon,
  }));
  const selectedMode = options.some(option => option.value === currentMode)
    ? currentMode
    : options[0].value;

  return (
    <div className="flex flex-col">
      <SettingTitle
        title="Panel mode"
        configField="mode"
        isDirty={isSetAtLevel}
        unsetConfigField={() => unsetConfigField('mode')}
        cta={newPillCta}
      />
      <ToggleButtonGroup
        aria-label="Panel mode"
        disallowEmptySelection
        items={items}
        onSelectionChange={keys => {
          const mode = keys.values().next().value;
          if (options.some(option => option.value === mode)) {
            onChangeMode(mode as PanelModes);
          }
        }}
        selectedKeys={[selectedMode]}
        selectionMode="single"
        size="md"
      />
    </div>
  );
};
